import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';
import { IconChevronDown, IconChevronUp, IconPlus, IconXMark } from './icons';

interface LineListLabels {
  /** The accessible name of row `index` (0-based). */
  item: (index: number) => string;
  moveUp: string;
  moveDown: string;
  remove: string;
  add: string;
}

/** Where the caret lands after the next render: a row and an offset in it. */
interface PendingFocus {
  index: number;
  caret: number | 'end';
}

/**
 * A row's identity: the caller's id for a row it handed in through `ids`, or a
 * number minted here for a row the editor made.
 */
type RowKey = string | number;

let lastKey = 0;
const newKey = () => ++lastKey;

/**
 * An ordered list of one-line items — ingredients, steps — edited one row at
 * a time. Enter splits a row at the caret, Backspace in an empty row removes
 * it, and pasting several lines makes one row per line, so a whole list copied
 * from a web page lands in one go. Rows grow with their text rather than
 * scroll; a row never holds a line break. The form owns the lines.
 *
 * A form that needs to tell an edited row from a new one passes `ids` beside
 * `value`, and reads them back as `onChange`'s second argument: a row keeps
 * its id through typing and moves, the first part of a split row keeps it, and
 * every row the editor made reports `null`.
 */
export function LineListEditor({
  label,
  value,
  ids,
  onChange,
  labels,
  numbered = false,
  maxLength,
}: {
  label: string;
  value: readonly string[];
  /** The caller's id for each row of `value`, or `null` for a row it has none for. */
  ids?: readonly (string | null)[];
  onChange: (lines: string[], ids: (string | null)[]) => void;
  labels: LineListLabels;
  /** Prefix each row with its position from 1, as steps are. */
  numbered?: boolean;
  maxLength?: number;
}) {
  // Stable row identities, so React keeps a moved row's DOM node — and its
  // focus — instead of rewriting the text of the rows in between.
  // They double as the rows' ids, seeded from `ids`.
  const [storedKeys, setKeys] = useState<RowKey[]>(() =>
    value.map((_, i) => ids?.[i] ?? newKey()),
  );
  // A list replaced from outside (a form reset) gets keys for its new rows.
  const keys =
    storedKeys.length === value.length
      ? storedKeys
      : value.map((_, i) => ids?.[i] ?? storedKeys[i] ?? i - value.length);
  const inputs = useRef(new Map<RowKey, HTMLTextAreaElement>());
  const pendingFocus = useRef<PendingFocus | null>(null);

  function commit(
    next: string[],
    nextKeys: RowKey[],
    focus: PendingFocus | null = null,
  ) {
    setKeys(nextKeys);
    pendingFocus.current = focus;
    onChange(
      next,
      nextKeys.map((k) => (typeof k === 'string' ? k : null)),
    );
  }

  /** Row `index` becomes `lines`, each its own row; the caret goes to `focus`. */
  function splice(index: number, lines: string[], focus: PendingFocus) {
    const next = [...value];
    next.splice(index, 1, ...lines);
    const nextKeys = [...keys];
    nextKeys.splice(
      index,
      1,
      keys[index] ?? newKey(),
      ...lines.slice(1).map(newKey),
    );
    commit(next, nextKeys, focus);
  }

  function remove(index: number, focus: PendingFocus | null) {
    commit(
      value.filter((_, i) => i !== index),
      keys.filter((_, i) => i !== index),
      focus,
    );
  }

  function move(index: number, to: number) {
    const next = [...value];
    const nextKeys = [...keys];
    [next[index], next[to]] = [next[to] ?? '', next[index] ?? ''];
    [nextKeys[index], nextKeys[to]] = [nextKeys[to] ?? 0, nextKeys[index] ?? 0];
    commit(next, nextKeys);
  }

  function onKeyDown(index: number, e: KeyboardEvent<HTMLTextAreaElement>) {
    // An IME still composing owns its Enter.
    if (e.nativeEvent.isComposing) return;
    const el = e.currentTarget;
    if (e.key === 'Enter') {
      e.preventDefault();
      splice(
        index,
        [el.value.slice(0, el.selectionStart), el.value.slice(el.selectionEnd)],
        { index: index + 1, caret: 0 },
      );
    } else if (e.key === 'Backspace' && el.value === '' && index > 0) {
      e.preventDefault();
      remove(index, { index: index - 1, caret: 'end' });
    }
  }

  function onPaste(index: number, e: ClipboardEvent<HTMLTextAreaElement>) {
    const pasted = e.clipboardData.getData('text/plain');
    if (!/[\r\n]/.test(pasted)) return;
    e.preventDefault();
    const el = e.currentTarget;
    const lines = (
      el.value.slice(0, el.selectionStart) +
      pasted +
      el.value.slice(el.selectionEnd)
    )
      .split(/\r?\n|\r/)
      .filter((line) => line.trim() !== '');
    if (lines.length === 0) lines.push('');
    splice(index, lines, { index: index + lines.length - 1, caret: 'end' });
  }

  function onInput(index: number, text: string) {
    // A keyboard that inserts the break without a keydown (some Android IMEs)
    // splits the row the same way Enter does.
    if (/[\r\n]/.test(text)) {
      const lines = text.split(/\r?\n|\r/);
      splice(index, lines, { index: index + lines.length - 1, caret: 0 });
      return;
    }
    commit(
      value.map((line, i) => (i === index ? text : line)),
      keys,
    );
  }

  // Each row as tall as its text, after every change and on a width change.
  useLayoutEffect(() => {
    const fit = () => {
      for (const el of inputs.current.values()) {
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      }
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [value]);

  useEffect(() => {
    const focus = pendingFocus.current;
    if (!focus) return;
    pendingFocus.current = null;
    const key = keys[focus.index];
    const el = key === undefined ? undefined : inputs.current.get(key);
    if (!el) return;
    el.focus();
    const at = focus.caret === 'end' ? el.value.length : focus.caret;
    el.setSelectionRange(at, at);
  }, [keys]);

  return (
    <fieldset className="line-list">
      <legend className="field-label">{label}</legend>
      {value.length > 0 && (
        <ol className="line-list-rows">
          {value.map((line, index) => {
            const key = keys[index] ?? index;
            return (
              <li key={key} className="line-list-row">
                {numbered && (
                  <span className="line-list-number" aria-hidden="true">
                    {index + 1}.
                  </span>
                )}
                <textarea
                  ref={(el) => {
                    if (el) inputs.current.set(key, el);
                    else inputs.current.delete(key);
                  }}
                  rows={1}
                  className="field-input line-list-input"
                  aria-label={labels.item(index)}
                  maxLength={maxLength}
                  value={line}
                  onChange={(e) => onInput(index, e.target.value)}
                  onKeyDown={(e) => onKeyDown(index, e)}
                  onPaste={(e) => onPaste(index, e)}
                />
                <div className="line-list-actions">
                  <button
                    type="button"
                    className="btn-icon-ghost"
                    aria-label={labels.moveUp}
                    title={labels.moveUp}
                    disabled={index === 0}
                    onClick={() => move(index, index - 1)}
                  >
                    <IconChevronUp />
                  </button>
                  <button
                    type="button"
                    className="btn-icon-ghost"
                    aria-label={labels.moveDown}
                    title={labels.moveDown}
                    disabled={index === value.length - 1}
                    onClick={() => move(index, index + 1)}
                  >
                    <IconChevronDown />
                  </button>
                  <button
                    type="button"
                    className="btn-icon-danger"
                    aria-label={labels.remove}
                    title={labels.remove}
                    onClick={() =>
                      remove(
                        index,
                        value.length > 1
                          ? { index: Math.max(0, index - 1), caret: 'end' }
                          : null,
                      )
                    }
                  >
                    <IconXMark />
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
      )}
      <button
        type="button"
        className="btn-ghost btn-sm line-list-add"
        onClick={() =>
          commit([...value, ''], [...keys, newKey()], {
            index: value.length,
            caret: 0,
          })
        }
      >
        <IconPlus />
        {labels.add}
      </button>
    </fieldset>
  );
}
